'use client';

import { useEffect, useRef } from 'react';

import { MorfiVortexLogo } from '@/components/logo/morfi-vortex-logo';

const VISIBILITY_THRESHOLD = 0.2;
const ANIMATION_SRC = '/assets/morfi-logo-story.html?embedded=1';
const IFRAME_CLASS = 'absolute inset-0 h-full w-full border-0';

function postPlayback(iframe: HTMLIFrameElement | null, playing: boolean): void {
  iframe?.contentWindow?.postMessage({ type: 'morfi-logo-story-playback', playing }, '*');
}

function createLogoStoryIframe(): HTMLIFrameElement {
  const iframe = document.createElement('iframe');
  iframe.title = 'Brand logo story animation';
  iframe.src = ANIMATION_SRC;
  iframe.setAttribute('sandbox', 'allow-scripts');
  iframe.className = IFRAME_CLASS;
  iframe.tabIndex = -1;
  iframe.setAttribute('aria-hidden', 'true');
  return iframe;
}

export function BrandKitHeroBackground(): React.ReactNode {
  const hostRef = useRef<HTMLDivElement>(null);
  const playingRef = useRef(true);

  useEffect(() => {
    const host = hostRef.current;
    if (host === null) {
      return;
    }

    const iframe = createLogoStoryIframe();
    const handleLoad = (): void => {
      postPlayback(iframe, playingRef.current);
    };
    const removeIframe = (): void => {
      iframe.removeEventListener('load', handleLoad);
      iframe.remove();
    };

    iframe.addEventListener('load', handleLoad);
    host.appendChild(iframe);

    if (typeof IntersectionObserver === 'undefined') {
      postPlayback(iframe, true);
      return removeIframe;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.target === host) {
            playingRef.current = entry.isIntersecting;
            postPlayback(iframe, playingRef.current);
          }
        }
      },
      { threshold: VISIBILITY_THRESHOLD },
    );

    observer.observe(host);
    return (): void => {
      observer.disconnect();
      removeIframe();
    };
  }, []);

  return (
    <div
      ref={hostRef}
      className="pointer-events-none absolute inset-0 overflow-hidden bg-background-secondary"
      aria-hidden="true"
    >
      <div className="absolute inset-0 flex items-start justify-center pt-24">
        <MorfiVortexLogo size={112} animateOnHover={false} className="text-foreground-primary" />
      </div>
    </div>
  );
}
